
import { Component, Prop, Vue } from "vue-property-decorator";
import ProjectLanguagesRepository from "../../repositories/implementations/ProjectLanguagesRepository";
import StatsRepository, { ProjectLanguageProgress } from "../../repositories/implementations/StatsRepository";
import { Project } from "../../entities/Project";
import { ProjectLanguage } from "../../entities/ProjectLanguage";
import { getFlagCode, getLanguageName, getLanguageLabel, getSupportedLanguages } from "../../utils/languageUtils";
import LanguageDropdown from "../../components/LanguageDropdown.vue";
import { toastSuccess, toastError } from "../../utils/toast";

@Component({ components: { LanguageDropdown } })
export default class LanguagesTab extends Vue {
  @Prop({ required: true }) project!: Project;

  @Prop({ required: true }) languages!: ProjectLanguage[];

  loading = false;

  progressByLang: Map<string, ProjectLanguageProgress> = new Map();

  langRepo = new ProjectLanguagesRepository();

  statsRepo = new StatsRepository();

  addLangModal = { open: false, code: "", error: "", loading: false };

  deleteLangModal = { open: false, code: "", loading: false };

  async created() {
    await this.loadProgress();
  }

  async loadProgress() {
    this.loading = true;
    try {
      const rows = await this.statsRepo.getProjectLanguageProgress(this.project.id);
      this.progressByLang = new Map(rows.map((r) => [r.languageCode, r]));
    } finally {
      this.loading = false;
    }
  }

  langPercent(code: string): number {
    return this.progressByLang.get(code)?.percent ?? 0;
  }

  progressColor(percent: number): string {
    if (percent >= 80) return "success";
    if (percent >= 40) return "warning";
    return "primary";
  }

  get sortedLanguages(): ProjectLanguage[] {
    return [...this.languages].sort((a, b) => this.languageName(a.languageCode).localeCompare(this.languageName(b.languageCode)));
  }

  get availableLanguages() {
    const added = new Set(this.languages.map((l) => l.languageCode));
    return getSupportedLanguages().filter((l) => !added.has(l.value));
  }

  flagCode(code: string): string {
    return getFlagCode(code);
  }

  languageName(code: string): string {
    return getLanguageName(code);
  }

  languageLabel(code: string): string {
    return getLanguageLabel(code);
  }

  goToLanguage(code: string) {
    this.$router.push({ name: "project-translations-by-language", params: { projectId: this.project.id, code } });
  }

  openDeleteLangModal(code: string) {
    this.deleteLangModal = { open: true, code, loading: false };
  }

  async submitAddLang() {
    if (!this.addLangModal.code.trim()) {
      this.addLangModal.error = "Language code is required.";
      return;
    }
    this.addLangModal.loading = true;
    try {
      await this.langRepo.add(this.project.id, this.addLangModal.code);
      toastSuccess("Language added.");
      this.addLangModal.open = false;
      this.addLangModal.code = "";
      this.$emit("languages-changed");
      await this.loadProgress();
    } catch (e: any) {
      this.addLangModal.error = e.message || "Failed to add language.";
    } finally {
      this.addLangModal.loading = false;
    }
  }

  async submitDeleteLang() {
    this.deleteLangModal.loading = true;
    try {
      await this.langRepo.remove(this.project.id, this.deleteLangModal.code);
      toastSuccess("Language removed.");
      this.deleteLangModal.open = false;
      this.$emit("languages-changed");
      await this.loadProgress();
    } catch (e: any) {
      toastError(e.message || "Failed to remove language.");
    } finally {
      this.deleteLangModal.loading = false;
    }
  }
}
